import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import TerritoiresPanel from "@/components/TerritoiresPanel";
import SuperAdminPanel from "@/components/SuperAdminPanel";
import { useAuth } from "@/contexts/AuthContext";
import {
  Building2, Users, Briefcase, FileSignature, Clock, FolderOpen,
  Receipt, CreditCard, CalendarOff, ShieldCheck, TrendingUp, Loader2,
} from "lucide-react";

const KPI = ({ icon: Icon, label, value, sub, color = "#C5A059", testId }) => (
  <div className="kpi-card bg-white border border-slate-200 rounded-xl p-6" data-testid={testId}>
    <div className="flex items-start justify-between">
      <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ background: `${color}1A`, color }}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">{label}</div>
    </div>
    <div className="mt-5 text-3xl font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </div>
);

const SectionTitle = ({ children }) => (
  <h2 className="text-xs font-bold uppercase tracking-[0.22em] text-slate-400 mb-3 mt-8">{children}</h2>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [kpi, setKpi] = useState(null);
  const [loading, setLoading] = useState(true);
  const canSeeTerritoires = ["admin", "gestionnaire_ge", "finance", "auditeur"].includes(user?.role);

  useEffect(() => {
    api.get("/kpi/overview").then(({ data }) => setKpi(data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="h-8 w-8 animate-spin text-[#C5A059]" /></div>;

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title={`Bonjour ${user?.name?.split(" ")[0] || ""}`}
        subtitle="Tableau de bord opérationnel O'SCOP GE — Pilotage RH, finance, GED & conformité du groupement."
        testId="dashboard-header"
      />

      <SectionTitle>Ressources humaines</SectionTitle>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <KPI icon={Building2} label="Entreprises" value={kpi?.rh?.entreprises ?? 0} sub="Membres du GE" color="#C5A059" testId="kpi-entreprises" />
        <KPI icon={Users} label="Salariés" value={kpi?.rh?.salaries ?? 0} sub="Mis à disposition" color="#0F172A" testId="kpi-salaries" />
        <KPI icon={CalendarOff} label="Absences" value={kpi?.rh?.absences ?? 0} sub="Suivies" color="#F59E0B" testId="kpi-absences" />
        <KPI icon={FileSignature} label="Conventions" value={kpi?.missions?.conventions ?? 0} sub="Mise à disposition" color="#10B981" testId="kpi-conventions" />
      </div>

      <SectionTitle>Missions & temps</SectionTitle>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <KPI icon={Briefcase} label="Missions total" value={kpi?.missions?.total ?? 0} color="#C5A059" testId="kpi-missions" />
        <KPI icon={Briefcase} label="En cours" value={kpi?.missions?.en_cours ?? 0} color="#10B981" testId="kpi-missions-encours" />
        <KPI icon={Clock} label="Heures (mois)" value={kpi?.temps?.heures_mois ?? 0} sub={`Validées: ${kpi?.temps?.heures_validees ?? 0}h`} color="#0F172A" testId="kpi-heures" />
        <KPI icon={TrendingUp} label="Taux validation" value={`${kpi?.temps?.taux_validation ?? 0}%`} color="#3B82F6" testId="kpi-validation" />
      </div>

      <SectionTitle>Finance & crédits</SectionTitle>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <KPI icon={Receipt} label="Émis" value={`${(kpi?.finance?.total_emis ?? 0).toLocaleString("fr-FR")} €`} sub="Appels à contribution" color="#C5A059" testId="kpi-emis" />
        <KPI icon={Receipt} label="Payé" value={`${(kpi?.finance?.total_paye ?? 0).toLocaleString("fr-FR")} €`} color="#10B981" testId="kpi-paye" />
        <KPI icon={Receipt} label="Solde dû" value={`${(kpi?.finance?.total_du ?? 0).toLocaleString("fr-FR")} €`} color="#EF4444" testId="kpi-du" />
        <KPI icon={CreditCard} label="Crédits services" value={kpi?.credits?.solde ?? 0} sub="Solde global" color="#0F172A" testId="kpi-credits" />
      </div>

      <SectionTitle>GED & conformité</SectionTitle>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <KPI icon={FolderOpen} label="Documents GED" value={kpi?.ged?.documents ?? 0} color="#C5A059" testId="kpi-documents" />
        <KPI icon={ShieldCheck} label="Audit logs" value={kpi?.conformite?.audit_logs ?? 0} sub="Traçabilité" color="#0F172A" testId="kpi-audit" />
        <KPI icon={Receipt} label="Frais" value={`${(kpi?.finance?.total_frais ?? 0).toLocaleString("fr-FR")} €`} color="#F59E0B" testId="kpi-frais" />
      </div>

      {canSeeTerritoires && (
        <>
          <SectionTitle>Vue par territoires Outre-mer</SectionTitle>
          <TerritoiresPanel />
        </>
      )}

      {(user?.role === "admin" || user?.role === "gestionnaire_ge") && (
        <>
          <SectionTitle>Pilotage opérationnel</SectionTitle>
          <SuperAdminPanel />
        </>
      )}

      <div className="mt-10 rounded-xl border border-slate-200 bg-gradient-to-r from-[#FDF9F1] to-white p-6 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between" data-testid="dashboard-banner">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.22em] text-[#C5A059]">ZOE — votre assistante</div>
          <h3 className="text-xl font-semibold text-slate-900 mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
            Générez vos conventions ou posez vos questions ESS
          </h3>
          <p className="text-sm text-slate-600 mt-1">Claude Sonnet 4.5 — droit du travail, L1253, contributions ESS et conformité.</p>
        </div>
        <a href="/app/assistant" className="inline-flex items-center gap-2 px-5 h-11 bg-[#C5A059] hover:bg-[#A88846] text-white rounded-md font-medium transition-colors" data-testid="dashboard-assistant-cta">
          Ouvrir l&apos;assistant
        </a>
      </div>
    </div>
  );
}
